import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { documentsApi, usersApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Upload,
  FileText,
  Download,
  Trash2,
  Loader2,
  FolderOpen,
  Users,
  Building,
  File,
  FileImage,
  FileArchive,
  Eye,
  X,
} from "lucide-react";

const Documents = () => {
  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [formData, setFormData] = useState({
    category: "firm-wide",
    target_user_id: "",
    description: "",
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [docsRes] = await Promise.all([
        documentsApi.getAll(),
      ]);
      setDocuments(docsRes.data);
      
      if (isAdmin) {
        const empRes = await usersApi.getAll();
        setEmployees(empRes.data);
      }
    } catch (error) {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error("File size must be less than 10MB");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Please select a file");
      return;
    }

    setUploading(true);
    try {
      const uploadData = new FormData();
      uploadData.append("file", selectedFile);
      uploadData.append("category", formData.category);
      uploadData.append("description", formData.description);
      if (formData.category === "personal" && formData.target_user_id) {
        uploadData.append("target_user_id", formData.target_user_id);
      }

      await documentsApi.upload(uploadData);
      toast.success("Document uploaded successfully");
      setDialogOpen(false);
      setSelectedFile(null);
      setFormData({ category: "firm-wide", target_user_id: "", description: "" });
      loadData();
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await documentsApi.download(doc.id);
      const { file_data, name, file_type } = response.data;
      
      // Convert base64 to blob
      const byteCharacters = atob(file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: file_type });
      
      // Download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const handleDelete = async (docId) => {
    try {
      await documentsApi.delete(docId);
      toast.success("Document deleted");
      loadData();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const handleView = async (doc) => {
    // Only allow viewing PDFs and images
    const viewableTypes = ['pdf', 'png', 'jpeg', 'jpg', 'gif', 'webp'];
    const fileExt = doc.file_type?.toLowerCase() || '';
    if (!viewableTypes.some(type => fileExt.includes(type))) {
      toast.error("This file type cannot be previewed. Downloading instead...");
      handleDownload(doc);
      return;
    }

    setViewerLoading(true);
    setViewerOpen(true);
    try {
      const response = await documentsApi.download(doc.id);
      const { file_data, name, file_type } = response.data;
      
      // Convert base64 to Blob URL (works better for large files)
      const byteCharacters = atob(file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      
      // Determine MIME type
      let mimeType = file_type;
      if (file_type?.includes('pdf') || file_type === 'application/pdf') {
        mimeType = 'application/pdf';
      } else if (file_type?.includes('png')) {
        mimeType = 'image/png';
      } else if (file_type?.includes('jpeg') || file_type?.includes('jpg')) {
        mimeType = 'image/jpeg';
      } else if (file_type?.includes('gif')) {
        mimeType = 'image/gif';
      } else if (file_type?.includes('webp')) {
        mimeType = 'image/webp';
      }
      
      const blob = new Blob([byteArray], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);
      
      setViewingDoc({ ...doc, dataUrl: blobUrl, file_type: mimeType, isBlobUrl: true });
    } catch (error) {
      console.error("View error:", error);
      toast.error("Failed to load document");
      setViewerOpen(false);
    } finally {
      setViewerLoading(false);
    }
  };

  // Cleanup blob URLs when viewer closes
  const closeViewer = () => {
    if (viewingDoc?.isBlobUrl && viewingDoc?.dataUrl) {
      URL.revokeObjectURL(viewingDoc.dataUrl);
    }
    setViewerOpen(false);
    setViewingDoc(null);
  };

  const isViewable = (fileType) => {
    return fileType?.includes('pdf') || fileType?.includes('image');
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes("image")) return <FileImage className="text-purple-500" size={20} />;
    if (fileType?.includes("pdf")) return <FileText className="text-red-500" size={20} />;
    if (fileType?.includes("zip") || fileType?.includes("archive")) return <FileArchive className="text-yellow-500" size={20} />;
    return <File className="text-blue-500" size={20} />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const firmWideDocuments = documents.filter((d) => d.category === "firm-wide");
  const personalDocuments = documents.filter((d) => d.category === "personal");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const DocumentTable = ({ docs, showEmployee = false }) => (
    <Table data-testid="documents-table">
      <TableHeader>
        <TableRow>
          <TableHead>Document</TableHead>
          {showEmployee && <TableHead>Employee</TableHead>}
          <TableHead>Description</TableHead>
          <TableHead>Size</TableHead>
          <TableHead>Uploaded</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {docs.map((doc) => (
          <TableRow key={doc.id} className="table-row-hover">
            <TableCell>
              <div className="flex items-center gap-3">
                {getFileIcon(doc.file_type)}
                <span className="font-medium">{doc.name}</span>
              </div>
            </TableCell>
            {showEmployee && (
              <TableCell className="text-muted-foreground">
                {doc.target_user_name || "-"}
              </TableCell>
            )}
            <TableCell className="text-muted-foreground max-w-xs truncate">
              {doc.description || "-"}
            </TableCell>
            <TableCell className="text-muted-foreground font-mono text-sm">
              {formatFileSize(doc.file_size)}
            </TableCell>
            <TableCell className="text-muted-foreground">
              {new Date(doc.created_at).toLocaleDateString()}
            </TableCell>
            <TableCell className="text-right">
              <div className="flex justify-end gap-1">
                {isViewable(doc.file_type) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleView(doc)}
                    className="text-muted-foreground hover:text-emerald-500"
                    data-testid={`view-doc-${doc.id}`}
                    title="View"
                  >
                    <Eye size={16} />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDownload(doc)}
                  className="text-muted-foreground hover:text-blue-500"
                  data-testid={`download-doc-${doc.id}`}
                  title="Download"
                >
                  <Download size={16} />
                </Button>
                {isAdmin && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-muted-foreground hover:text-rose-600"
                        data-testid={`delete-doc-${doc.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Document</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete "{doc.name}"? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDelete(doc.id)}
                          className="bg-rose-600 hover:bg-rose-700"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
        {docs.length === 0 && (
          <TableRow>
            <TableCell colSpan={showEmployee ? 6 : 5} className="text-center py-8 text-muted-foreground">
              No documents found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Documents</h1>
          <p className="text-muted-foreground mt-1">
            {isAdmin ? "Manage firm and employee documents" : "Access your documents"}
          </p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="upload-doc-btn">
                <Upload size={18} />
                Upload Document
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid="upload-dialog">
              <DialogHeader>
                <DialogTitle>Upload Document</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) =>
                      setFormData({ ...formData, category: value, target_user_id: "" })
                    }
                  >
                    <SelectTrigger data-testid="select-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="firm-wide">
                        <div className="flex items-center gap-2">
                          <Building size={16} />
                          Firm-Wide (visible to all)
                        </div>
                      </SelectItem>
                      <SelectItem value="personal">
                        <div className="flex items-center gap-2">
                          <Users size={16} />
                          Personal (specific employee)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {formData.category === "personal" && (
                  <div className="space-y-2">
                    <Label>Employee</Label>
                    <Select
                      value={formData.target_user_id}
                      onValueChange={(value) =>
                        setFormData({ ...formData, target_user_id: value })
                      }
                    >
                      <SelectTrigger data-testid="select-employee">
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees
                          .filter((e) => e.role === "employee")
                          .map((emp) => (
                            <SelectItem key={emp.id} value={emp.id}>
                              {emp.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="file">File</Label>
                  <Input
                    id="file"
                    type="file"
                    onChange={handleFileChange}
                    className="cursor-pointer"
                    data-testid="file-input"
                  />
                  {selectedFile && (
                    <p className="text-sm text-muted-foreground">
                      Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (optional)</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="Brief description of the document"
                    rows={2}
                    data-testid="description-input"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={uploading || !selectedFile}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="submit-upload-btn"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      "Upload"
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                <FolderOpen className="text-blue-500" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Documents</p>
                <p className="text-2xl font-bold text-foreground">{documents.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <Building className="text-emerald-500" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Firm-Wide</p>
                <p className="text-2xl font-bold text-foreground">{firmWideDocuments.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                <Users className="text-purple-500" size={24} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Personal</p>
                <p className="text-2xl font-bold text-foreground">{personalDocuments.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Document tabs */}
      <Card>
        <Tabs defaultValue="firm-wide" className="w-full">
          <CardHeader>
            <TabsList>
              <TabsTrigger value="firm-wide" className="gap-2" data-testid="tab-firm-wide">
                <Building size={16} />
                Firm-Wide Documents
              </TabsTrigger>
              <TabsTrigger value="personal" className="gap-2" data-testid="tab-personal">
                <Users size={16} />
                {isAdmin ? "Employee Documents" : "My Documents"}
              </TabsTrigger>
            </TabsList>
          </CardHeader>
          <CardContent>
            <TabsContent value="firm-wide" className="mt-0">
              <DocumentTable docs={firmWideDocuments} />
            </TabsContent>
            <TabsContent value="personal" className="mt-0">
              <DocumentTable docs={personalDocuments} showEmployee={isAdmin} />
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>

      {/* Document Viewer Modal */}
      {viewerOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg w-full max-w-5xl h-[85vh] flex flex-col border border-border">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-3">
                {viewingDoc && getFileIcon(viewingDoc.file_type)}
                <span className="font-medium text-lg text-foreground">{viewingDoc?.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => viewingDoc && handleDownload(viewingDoc)}
                  className="gap-2"
                >
                  <Download size={16} />
                  Download
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeViewer}
                >
                  <X size={20} />
                </Button>
              </div>
            </div>
            
            {/* Content - PDF/images always shown on white background for readability */}
            <div className="flex-1 overflow-hidden bg-white">
              {viewerLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                </div>
              ) : viewingDoc?.file_type?.includes('pdf') ? (
                <object
                  data={viewingDoc.dataUrl}
                  type="application/pdf"
                  className="w-full h-full"
                  title={viewingDoc.name}
                >
                  <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-4">
                    <p>PDF preview not supported in this browser.</p>
                    <Button onClick={() => handleDownload(viewingDoc)} className="gap-2">
                      <Download size={16} />
                      Download PDF
                    </Button>
                  </div>
                </object>
              ) : viewingDoc?.file_type?.includes('image') ? (
                <div className="flex items-center justify-center h-full p-4">
                  <img
                    src={viewingDoc.dataUrl}
                    alt={viewingDoc.name}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  Cannot preview this file type
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;
